import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

type CookieConsent = 'accepted' | 'declined' | null;

export interface CookiePreferences {
  /** Remember the chosen language. */
  preferences: boolean;
  /** Remember light or dark. */
  theme: boolean;
  /** Load Google Analytics. */
  analytics: boolean;
}

interface CookieConsentContextType {
  cookieConsent: CookieConsent;
  cookiePreferences: CookiePreferences;
  setCookieConsent: (consent: CookieConsent, preferences?: CookiePreferences) => void;
  hasConsented: boolean;
  showBanner: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const NONE: CookiePreferences = { preferences: false, theme: false, analytics: false };

/** Everything this site may write, so declining can clear all of it. */
const STORAGE_KEYS = ['cookieConsent', 'cookiePreferences', 'cookieConsentData', 'language', 'themeMode'];

/**
 * The site stores nothing in the browser until the visitor allows it, and
 * stores only what they allowed. Its own preferences live in localStorage
 * under the keys above; the only actual cookies come from Google Analytics,
 * which is loaded exclusively while `analytics` is true — see
 * GoogleAnalytics.tsx, which also clears those cookies on withdrawal.
 */
export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cookieConsent, setCookieConsentState] = useState<CookieConsent>(null);
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>(NONE);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    try {
      const savedConsent = localStorage.getItem('cookieConsent') as CookieConsent;

      if (!savedConsent) {
        setShowBanner(true);
        return;
      }

      setCookieConsentState(savedConsent);

      const savedPreferences = localStorage.getItem('cookiePreferences');
      if (savedPreferences) {
        const parsed = JSON.parse(savedPreferences);
        setCookiePreferences({
          preferences: parsed.preferences === true,
          theme: parsed.theme === true,
          analytics: parsed.analytics === true,
        });
      }
    } catch {
      // Storage blocked: behave as if nothing was ever stored.
      setShowBanner(true);
    }
  }, []);

  const setCookieConsent = useCallback((consent: CookieConsent, preferences?: CookiePreferences) => {
    setCookieConsentState(consent);
    setShowBanner(false);

    try {
      if (consent === 'accepted') {
        const finalPreferences = preferences ?? NONE;
        setCookiePreferences(finalPreferences);

        localStorage.setItem('cookieConsent', consent);
        localStorage.setItem('cookiePreferences', JSON.stringify(finalPreferences));
        // Proof of consent: what was agreed to, and when.
        localStorage.setItem(
          'cookieConsentData',
          JSON.stringify({ timestamp: new Date().toISOString(), consent, preferences: finalPreferences })
        );

        // Withdrawing a single preference must drop what it had stored.
        if (!finalPreferences.theme) localStorage.removeItem('themeMode');
        if (!finalPreferences.preferences) localStorage.removeItem('language');
        return;
      }

      // Declining clears everything, including anything stored under an
      // earlier consent.
      setCookiePreferences(NONE);
      STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    } catch {
      // Storage unavailable - the choice applies to this visit only.
    }
  }, []);

  return (
    <CookieConsentContext.Provider
      value={{
        cookieConsent,
        cookiePreferences,
        setCookieConsent,
        hasConsented: cookieConsent === 'accepted',
        showBanner,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
